import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  X,
  Building2,
  Scale,
  UserCheck,
  Shield,
  UserX,
  Gavel,
  ChevronDown,
  CircleDot,
  Calendar,
  LogOut,
  User,
  Settings,
  FileText,
  CreditCard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import ProfileSettings from '../../../components/ProfileSettings';
import ChargesDropdown from './ChargesDropdown';
import CourtDepartmentDropdown from './CourtDepartmentDropdown';
import StatusDropdown from './StatusDropdown';
import CaseTypeDropdown from './CaseTypeDropdown';
import JudgesDropdown from './JudgesDropdown';
import ProsecutorsDropdown from './ProsecutorsDropdown';
import DefenseDropdown from './DefenseDropdown';
import DefendantsDropdown from './DefendantsDropdown';
import SentencingTypesDropdown from './SentencingTypesDropdown';
import DateRangeDropdown from './DateRangeDropdown';
import RecentSearchesDropdown from './RecentSearchesDropdown';
import PropTypes from 'prop-types';
import { FiltersProps } from '../../../types/propTypes';
import { formatDateWithOptions } from '../../../utils/dateUtils';

const NewHeader = ({
  searchQuery,
  setSearchQuery,
  onSearch,
  filters,
  setFilters,
  courts = [],
  recentSearches = [],
  onSelectRecentSearch,
  onClearRecentSearches,
  searchExecuted = false,
  setSearchExecuted,
  onClearSourceCase
}) => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [showChargesDropdown, setShowChargesDropdown] = useState(false);
  const [showCourtDepartmentDropdown, setShowCourtDepartmentDropdown] =
    useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCaseTypeDropdown, setShowCaseTypeDropdown] = useState(false);
  const [showJudgesDropdown, setShowJudgesDropdown] = useState(false);
  const [showProsecutorsDropdown, setShowProsecutorsDropdown] = useState(false);
  const [showDefenseDropdown, setShowDefenseDropdown] = useState(false);
  const [showDefendantsDropdown, setShowDefendantsDropdown] = useState(false);
  const [showSentencingTypesDropdown, setShowSentencingTypesDropdown] =
    useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showLegalDisclaimers, setShowLegalDisclaimers] = useState(false);

  // Prevent background scrolling when modals are open
  useEffect(() => {
    if (showProfileSettings || showLegalDisclaimers) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;
      const originalTop = window.getComputedStyle(document.body).top;
      const scrollY = window.scrollY;

      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      const preventTouchMove = (e) => {
        const modal = document.querySelector('[data-modal-content]');
        if (modal && !modal.contains(e.target)) {
          e.preventDefault();
        }
      };

      document.addEventListener('touchmove', preventTouchMove, {
        passive: false
      });
      document.addEventListener('touchstart', preventTouchMove, {
        passive: false
      });

      return () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.removeEventListener('touchmove', preventTouchMove);
        document.removeEventListener('touchstart', preventTouchMove);
        window.scrollTo(0, scrollY);
      };
    }
  }, [showProfileSettings, showLegalDisclaimers]);

  const headerRef = useRef(null);

  // Refs for dropdown positioning
  const chargesButtonRef = useRef(null);
  const courtDepartmentButtonRef = useRef(null);
  const statusButtonRef = useRef(null);
  const caseTypeButtonRef = useRef(null);
  const judgesButtonRef = useRef(null);
  const prosecutorsButtonRef = useRef(null);
  const defenseButtonRef = useRef(null);
  const defendantsButtonRef = useRef(null);
  const sentencingTypesButtonRef = useRef(null);
  const dateButtonRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return; // Don't close dropdowns if clicking on a filter button
      }

      // Check if click is outside user menu specifically
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }

      // Check if click is outside the main header area
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setShowChargesDropdown(false);
        setShowCourtDepartmentDropdown(false);
        setShowStatusDropdown(false);
        setShowCaseTypeDropdown(false);
        setShowJudgesDropdown(false);
        setShowProsecutorsDropdown(false);
        setShowDefenseDropdown(false);
        setShowDefendantsDropdown(false);
        setShowSentencingTypesDropdown(false);
        setShowDateDropdown(false);
        setShowRecentSearches(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // Handle search
  const handleSearch = () => {
    onSearch(searchQuery, filters, 1, true, true);
    setShowRecentSearches(false);
  };

  // Handle key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle charges change
  const handleChargesChange = (newCharges) => {
    setFilters((prev) => ({ ...prev, charges: newCharges }));
    // Trigger search immediately
    onSearch(searchQuery, { ...filters, charges: newCharges }, 1, true, true);
  };

  // Handle charge logic change
  const handleChargeLogicChange = (newLogic) => {
    setFilters((prev) => ({ ...prev, chargeLogic: newLogic }));
    // Trigger search immediately
    onSearch(searchQuery, { ...filters, chargeLogic: newLogic }, 1, true, true);
  };

  // Handle court change
  const handleCourtChange = (newCourt) => {
    setFilters((prev) => ({ ...prev, courthouse: newCourt }));
    // Trigger search immediately
    onSearch(searchQuery, { ...filters, courthouse: newCourt }, 1, true, true);
  };

  // Handle status change
  const handleStatusChange = (newStatus) => {
    setFilters((prev) => ({ ...prev, caseStatus: newStatus }));
    // Trigger search immediately
    onSearch(searchQuery, { ...filters, caseStatus: newStatus }, 1, true, true);
  };

  // Handle case type change
  const handleCaseTypeChange = (newCaseType) => {
    setFilters((prev) => ({ ...prev, caseType: newCaseType }));
    // Trigger search immediately
    onSearch(searchQuery, { ...filters, caseType: newCaseType }, 1, true, true);
  };

  // Handle judges change
  const handleJudgesChange = (newJudges) => {
    setFilters((prev) => ({ ...prev, judges: newJudges }));
    // Trigger search immediately
    onSearch(searchQuery, { ...filters, judges: newJudges }, 1, true, true);
  };

  // Handle prosecutors change
  const handleProsecutorsChange = (newProsecutors) => {
    setFilters((prev) => ({ ...prev, prosecutingAttorneys: newProsecutors }));
    // Trigger search immediately
    onSearch(
      searchQuery,
      { ...filters, prosecutingAttorneys: newProsecutors },
      1,
      true,
      true
    );
  };

  // Handle defense attorneys change
  const handleDefenseChange = (newDefense) => {
    setFilters((prev) => ({ ...prev, defenseAttorneys: newDefense }));
    // Trigger search immediately
    onSearch(
      searchQuery,
      { ...filters, defenseAttorneys: newDefense },
      1,
      true,
      true
    );
  };

  // Handle defendants change
  const handleDefendantsChange = (newDefendants) => {
    setFilters((prev) => ({ ...prev, defendants: newDefendants }));
    // Trigger search immediately
    onSearch(
      searchQuery,
      { ...filters, defendants: newDefendants },
      1,
      true,
      true
    );
  };

  // Handle departments change
  const handleDepartmentsChange = (newDepartments) => {
    setFilters((prev) => ({ ...prev, departments: newDepartments }));
    // Trigger search immediately
    onSearch(
      searchQuery,
      { ...filters, departments: newDepartments },
      1,
      true,
      true
    );
  };

  // Handle sentencing types change
  const handleSentencingTypesChange = (newSentencingTypes) => {
    setFilters((prev) => ({ ...prev, sentencingTypes: newSentencingTypes }));
    // Trigger search immediately
    onSearch(
      searchQuery,
      { ...filters, sentencingTypes: newSentencingTypes },
      1,
      true,
      true
    );
  };

  // Clear all filters and search query
  const clearAllFilters = () => {
    const clearedFilters = {
      jurisdiction: filters.jurisdiction, // Preserve current jurisdiction
      courthouse: '',
      departments: [],
      caseStatus: '',
      caseType: '',
      judges: [],
      defendants: [],
      prosecutingAttorneys: [],
      defenseAttorneys: [],
      charges: [],
      chargeLogic: 'OR',
      sentencingTypes: [],
      dateFrom: null,
      dateTo: null,
      similarTo: null,
      demographics: {
        race: null,
        gender: null,
        minAge: null,
        maxAge: null
      }
    };

    setFilters(clearedFilters);
    setSearchQuery('');
    setSearchExecuted(false);

    // Clear source case when exiting similarity mode
    if (filters.similarTo && onClearSourceCase) {
      onClearSourceCase();
    }

    // Trigger search with cleared state
    onSearch('', clearedFilters, 1, true, true);
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.courthouse) count++;
    if (filters.caseStatus) count++;
    if (filters.caseType) count++;
    if (filters.charges?.length > 0) count++;
    if (filters.judges?.length > 0) count++;
    if (filters.prosecutingAttorneys?.length > 0) count++;
    if (filters.defenseAttorneys?.length > 0) count++;
    if (filters.defendants?.length > 0) count++;
    if (filters.departments?.length > 0) count++;
    if (filters.sentencingTypes?.length > 0) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.similarTo) count++;
    if (filters.demographics?.race) count++;
    if (filters.demographics?.minAge || filters.demographics?.maxAge) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Check if search query is active and has been executed
  const hasActiveSearch =
    searchExecuted && searchQuery && searchQuery.trim().length > 0;

  // Get clear button text based on state
  const getClearButtonText = () => {
    const filterCount = getActiveFilterCount();
    if (hasActiveSearch && filterCount > 0) {
      return 'Clear Search & Filters';
    } else if (hasActiveSearch) {
      return 'Clear Search';
    } else if (filterCount > 0) {
      return 'Clear All Filters';
    }
    return 'Clear All Filters';
  };

  return (
    <div ref={headerRef} className="bg-white border-b border-gray-200">
      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-1.5 sm:py-4">
        <div className="flex items-center gap-1.5 sm:gap-4 mb-2 sm:mb-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 sm:gap-4 hover:opacity-80 transition-opacity"
            title="Go to Briefed homepage"
          >
            <img
              src="/briefed_logo_transp.png"
              alt="Briefed Logo"
              className="h-6 sm:h-10 w-auto"
            />
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-3xl font-light tracking-tight text-gray-900">
                  Briefed
                </h1>
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                  LA County
                </span>
              </div>
              <p className="text-xs text-gray-600 font-medium">
                Court Intelligence
              </p>
            </div>
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => navigate('/reports')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Charge Reports"
            >
              <Gavel className="h-4 w-4" />
              <span className="hidden sm:inline">Overall Reports</span>
            </button>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="User Menu"
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {currentUser?.displayName ||
                    currentUser?.email?.split('@')[0] ||
                    'User'}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      <div className="font-medium">
                        {currentUser?.displayName || 'User'}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {currentUser?.email}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileSettings(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Profile Settings
                    </button>
                    <button
                      onClick={() => {
                        setShowLegalDisclaimers(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Legal Disclaimers
                    </button>
                    <button
                      onClick={() => {
                        navigate('/subscription');
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <CreditCard className="h-4 w-4" />
                      Manage Subscription
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-2 sm:mb-4">
          <div className="flex gap-1.5 sm:gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                onFocus={() => setShowRecentSearches(true)}
                placeholder="Search anything"
                className="w-full pl-7 sm:pl-10 pr-2.5 sm:pr-4 py-1.5 sm:py-3 border border-gray-300 rounded-lg text-base sm:text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5 sm:w-5 sm:h-5" />
                </button>
              )}

              {/* Recent Searches Dropdown */}
              {showRecentSearches && recentSearches.length > 0 && (
                <div className="absolute top-full left-0 mt-1 z-50">
                  <RecentSearchesDropdown
                    recentSearches={recentSearches}
                    onSelectSearch={(search) => {
                      onSelectRecentSearch(search);
                      setShowRecentSearches(false);
                    }}
                    onClearSearches={onClearRecentSearches}
                    isOpen={true}
                    onClose={() => setShowRecentSearches(false)}
                  />
                </div>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="px-3 sm:px-6 py-1.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-xs sm:text-base"
            >
              Search
            </button>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="space-y-2 sm:space-y-4 mb-2 sm:mb-4">
          {/* Court & Case Filters */}
          <div className="space-y-1.5 sm:space-y-3">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="h-px bg-gray-200 flex-1"></div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1.5 sm:px-2">
                Court & Case Details
              </span>
              <div className="h-px bg-gray-200 flex-1"></div>
            </div>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <div className="relative">
                <button
                  ref={courtDepartmentButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle court department dropdown
                    setShowCourtDepartmentDropdown(
                      !showCourtDepartmentDropdown
                    );
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.courthouse || filters.departments?.length > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Courthouse</span>
                  {(filters.courthouse || filters.departments?.length > 0) && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {filters.courthouse
                        ? filters.departments?.length > 0
                          ? filters.departments.length + 1
                          : 1
                        : filters.departments?.length || 0}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showCourtDepartmentDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <CourtDepartmentDropdown
                  isOpen={showCourtDepartmentDropdown}
                  onClose={() => setShowCourtDepartmentDropdown(false)}
                  selectedCourt={filters.courthouse || ''}
                  selectedDepartments={filters.departments || []}
                  onCourtChange={handleCourtChange}
                  onDepartmentsChange={handleDepartmentsChange}
                  courts={courts}
                  jurisdiction={filters.jurisdiction}
                  buttonRef={courtDepartmentButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={statusButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle status dropdown
                    setShowStatusDropdown(!showStatusDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.caseStatus
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <CircleDot className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Status</span>
                  {filters.caseStatus && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      1
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showStatusDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <StatusDropdown
                  isOpen={showStatusDropdown}
                  onClose={() => setShowStatusDropdown(false)}
                  selectedStatus={filters.caseStatus || ''}
                  onStatusChange={handleStatusChange}
                  buttonRef={statusButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={caseTypeButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle case type dropdown
                    setShowCaseTypeDropdown(!showCaseTypeDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.caseType
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Scale className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Case Type</span>
                  {filters.caseType && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      1
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showCaseTypeDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <CaseTypeDropdown
                  isOpen={showCaseTypeDropdown}
                  onClose={() => setShowCaseTypeDropdown(false)}
                  selectedCaseType={filters.caseType || ''}
                  onCaseTypeChange={handleCaseTypeChange}
                  buttonRef={caseTypeButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={chargesButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle charges dropdown
                    setShowChargesDropdown(!showChargesDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.charges?.length > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Scale className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Charges</span>
                  {filters.charges?.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {filters.charges.length}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showChargesDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <ChargesDropdown
                  isOpen={showChargesDropdown}
                  onClose={() => setShowChargesDropdown(false)}
                  selectedCharges={filters.charges || []}
                  onChargesChange={handleChargesChange}
                  chargeLogic={filters.chargeLogic || 'OR'}
                  onChargeLogicChange={handleChargeLogicChange}
                  buttonRef={chargesButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={sentencingTypesButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle sentencing types dropdown
                    setShowSentencingTypesDropdown(
                      !showSentencingTypesDropdown
                    );
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.sentencingTypes?.length > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Gavel className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Sentencing</span>
                  {filters.sentencingTypes?.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {filters.sentencingTypes.length}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showSentencingTypesDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <SentencingTypesDropdown
                  isOpen={showSentencingTypesDropdown}
                  onClose={() => setShowSentencingTypesDropdown(false)}
                  selectedSentencingTypes={filters.sentencingTypes || []}
                  onSentencingTypesChange={handleSentencingTypesChange}
                  buttonRef={sentencingTypesButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={dateButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle date dropdown
                    setShowDateDropdown(!showDateDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.dateFrom || filters.dateTo
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Date</span>
                  {(filters.dateFrom || filters.dateTo) && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      1
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showDateDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <DateRangeDropdown
                  isOpen={showDateDropdown}
                  onClose={() => setShowDateDropdown(false)}
                  dateFrom={filters.dateFrom}
                  dateTo={filters.dateTo}
                  onDateFromChange={(dateFrom) => {
                    setFilters((prev) => ({ ...prev, dateFrom }));
                    onSearch(
                      searchQuery,
                      { ...filters, dateFrom },
                      1,
                      true,
                      true
                    );
                  }}
                  onDateToChange={(dateTo) => {
                    setFilters((prev) => ({ ...prev, dateTo }));
                    onSearch(
                      searchQuery,
                      { ...filters, dateTo },
                      1,
                      true,
                      true
                    );
                  }}
                  onClear={() => {
                    setFilters((prev) => ({
                      ...prev,
                      dateFrom: null,
                      dateTo: null
                    }));
                    onSearch(
                      searchQuery,
                      { ...filters, dateFrom: null, dateTo: null },
                      1,
                      true,
                      true
                    );
                  }}
                  buttonRef={dateButtonRef}
                />
              </div>
            </div>
          </div>

          {/* Case Participants Filters */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="h-px bg-gray-200 flex-1"></div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider px-1.5 sm:px-2">
                Case Participants
              </span>
              <div className="h-px bg-gray-200 flex-1"></div>
            </div>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <div className="relative">
                <button
                  ref={judgesButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle judges dropdown
                    setShowJudgesDropdown(!showJudgesDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.judges?.length > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Gavel className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Judges</span>
                  {filters.judges?.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {filters.judges.length}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showJudgesDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <JudgesDropdown
                  isOpen={showJudgesDropdown}
                  onClose={() => setShowJudgesDropdown(false)}
                  selectedJudges={filters.judges || []}
                  onJudgesChange={handleJudgesChange}
                  buttonRef={judgesButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={prosecutorsButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle prosecutors dropdown
                    setShowProsecutorsDropdown(!showProsecutorsDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.prosecutingAttorneys?.length > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <UserCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Prosecutors</span>
                  {filters.prosecutingAttorneys?.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {filters.prosecutingAttorneys.length}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showProsecutorsDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <ProsecutorsDropdown
                  isOpen={showProsecutorsDropdown}
                  onClose={() => setShowProsecutorsDropdown(false)}
                  selectedProsecutors={filters.prosecutingAttorneys || []}
                  onProsecutorsChange={handleProsecutorsChange}
                  buttonRef={prosecutorsButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={defenseButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefendantsDropdown(false);
                    setShowSentencingTypesDropdown(false);
                    setShowDateDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle defense attorneys dropdown
                    setShowDefenseDropdown(!showDefenseDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.defenseAttorneys?.length > 0
                      ? 'bg-blue-50 border-blue-200 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Defense</span>
                  {filters.defenseAttorneys?.length > 0 && (
                    <span className="bg-blue-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {filters.defenseAttorneys.length}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showDefenseDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <DefenseDropdown
                  isOpen={showDefenseDropdown}
                  onClose={() => setShowDefenseDropdown(false)}
                  selectedDefense={filters.defenseAttorneys || []}
                  onDefenseChange={handleDefenseChange}
                  buttonRef={defenseButtonRef}
                />
              </div>

              <div className="relative">
                <button
                  ref={defendantsButtonRef}
                  data-filter-button
                  onClick={() => {
                    // Close all other dropdowns first
                    setShowChargesDropdown(false);
                    setShowCourtDepartmentDropdown(false);
                    setShowStatusDropdown(false);
                    setShowCaseTypeDropdown(false);
                    setShowJudgesDropdown(false);
                    setShowProsecutorsDropdown(false);
                    setShowDefenseDropdown(false);
                    setShowRecentSearches(false);
                    // Toggle defendants dropdown
                    setShowDefendantsDropdown(!showDefendantsDropdown);
                  }}
                  className={`flex items-center gap-1 sm:gap-2 px-1.5 sm:px-4 py-1 sm:py-2 rounded-lg border transition-all duration-200 text-xs sm:text-base ${
                    filters.defendants?.length > 0 ||
                    filters.demographics?.race ||
                    filters.demographics?.minAge ||
                    filters.demographics?.maxAge
                      ? 'bg-purple-50 border-purple-200 text-purple-700 shadow-sm'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                >
                  <UserX className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="font-medium">Defendants</span>
                  {(filters.defendants?.length > 0 ||
                    filters.demographics?.race ||
                    filters.demographics?.minAge ||
                    filters.demographics?.maxAge) && (
                    <span className="bg-purple-600 text-white text-xs font-semibold rounded-full px-1 sm:px-2 py-0.5 min-w-[14px] sm:min-w-[20px] text-center">
                      {(() => {
                        let count = filters.defendants?.length || 0;
                        if (filters.demographics?.race) count++;
                        if (filters.demographics?.gender) count++;
                        if (
                          filters.demographics?.minAge ||
                          filters.demographics?.maxAge
                        )
                          count++;
                        return count;
                      })()}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-2 h-2 sm:w-3 sm:h-3 transition-transform duration-200 ${showDefendantsDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                <DefendantsDropdown
                  isOpen={showDefendantsDropdown}
                  onClose={() => setShowDefendantsDropdown(false)}
                  selectedDefendants={filters.defendants || []}
                  onDefendantsChange={handleDefendantsChange}
                  demographics={filters.demographics}
                  onDemographicsChange={(newDemographics) => {
                    setFilters((prev) => ({
                      ...prev,
                      demographics: newDemographics
                    }));
                    // Trigger search immediately
                    onSearch(
                      searchQuery,
                      { ...filters, demographics: newDemographics },
                      1,
                      true,
                      true
                    );
                  }}
                  buttonRef={defendantsButtonRef}
                />
              </div>
            </div>
          </div>

          {/* Clear All Button */}
          {(activeFilterCount > 0 || hasActiveSearch) && (
            <div className="flex justify-center pt-3">
              <button
                data-filter-button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <X className="w-4 h-4" />
                {getClearButtonText()}
              </button>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {(activeFilterCount > 0 || hasActiveSearch) && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {hasActiveSearch && (
              <span className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-green-100 text-green-800 text-xs sm:text-sm rounded-full">
                Search: &ldquo;{searchQuery}&rdquo;
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSearchExecuted(false);
                    onSearch('', filters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.similarTo && (
              <span className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-orange-100 text-orange-800 text-xs sm:text-sm rounded-full">
                Similar to: {filters.similarTo}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, similarTo: null };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-orange-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.charges?.map((charge) => (
              <span
                key={charge.code}
                className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm rounded-full"
              >
                {charge.code}
                <button
                  onClick={() => {
                    const newCharges = filters.charges.filter(
                      (c) => c.code !== charge.code
                    );
                    const updatedFilters = { ...filters, charges: newCharges };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            ))}
            {filters.courthouse && (
              <span className="inline-flex items-center px-2 sm:px-3 py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm rounded-full">
                Court: {filters.courthouse}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, courthouse: '' };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.departments?.map((dept) => (
              <span
                key={dept}
                className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm rounded-full"
              >
                Dept {dept}
                <button
                  onClick={() => {
                    const updatedFilters = {
                      ...filters,
                      departments:
                        filters.departments?.filter((d) => d !== dept) || []
                    };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            ))}
            {filters.caseStatus && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {filters.caseStatus === 'Open'
                  ? 'Open Cases'
                  : filters.caseStatus === 'Closed'
                    ? 'Closed Cases'
                    : `Status: ${filters.caseStatus}`}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, caseStatus: '' };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.caseType && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {filters.caseType === 'Felony'
                  ? 'Felony Cases'
                  : filters.caseType === 'Misdemeanor'
                    ? 'Misdemeanor Cases'
                    : `Type: ${filters.caseType}`}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, caseType: '' };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.judges?.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Judges: {filters.judges.length}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, judges: [] };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.prosecutingAttorneys?.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Prosecutors: {filters.prosecutingAttorneys.length}
                <button
                  onClick={() => {
                    const updatedFilters = {
                      ...filters,
                      prosecutingAttorneys: []
                    };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.defenseAttorneys?.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Defense: {filters.defenseAttorneys.length}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, defenseAttorneys: [] };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.defendants?.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Defendants: {filters.defendants.length}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, defendants: [] };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.demographics?.race && (
              <span className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-purple-100 text-purple-800 text-xs sm:text-sm rounded-full">
                Race: {filters.demographics.race}
                <button
                  onClick={() => {
                    const updatedFilters = {
                      ...filters,
                      demographics: {
                        ...filters.demographics,
                        race: null
                      }
                    };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.demographics?.gender && (
              <span className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-purple-100 text-purple-800 text-xs sm:text-sm rounded-full">
                Gender: {filters.demographics.gender}
                <button
                  onClick={() => {
                    const updatedFilters = {
                      ...filters,
                      demographics: {
                        ...filters.demographics,
                        gender: null
                      }
                    };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {(filters.demographics?.minAge || filters.demographics?.maxAge) && (
              <span className="inline-flex items-center px-1.5 sm:px-3 py-0.5 sm:py-1 bg-purple-100 text-purple-800 text-xs sm:text-sm rounded-full">
                Age: {filters.demographics.minAge || '0'}-
                {filters.demographics.maxAge || '120'}
                <button
                  onClick={() => {
                    const updatedFilters = {
                      ...filters,
                      demographics: {
                        ...filters.demographics,
                        minAge: null,
                        maxAge: null
                      }
                    };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {filters.sentencingTypes?.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Sentencing: {filters.sentencingTypes.length}
                <button
                  onClick={() => {
                    const updatedFilters = { ...filters, sentencingTypes: [] };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
            {(filters.dateFrom || filters.dateTo) && (
              <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Date:{' '}
                {filters.dateFrom && filters.dateTo
                  ? `${formatDateWithOptions(filters.dateFrom, { month: 'short', day: 'numeric', year: 'numeric' })} - ${formatDateWithOptions(filters.dateTo, { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : filters.dateFrom
                    ? `From ${formatDateWithOptions(filters.dateFrom, { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : `Until ${formatDateWithOptions(filters.dateTo, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                <button
                  onClick={() => {
                    const updatedFilters = {
                      ...filters,
                      dateFrom: null,
                      dateTo: null
                    };
                    setFilters(updatedFilters);
                    onSearch(searchQuery, updatedFilters, 1, true, true);
                  }}
                  className="ml-1 sm:ml-2 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowProfileSettings(false);
            }
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setShowProfileSettings(false);
            }
          }}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto modal-content"
            data-modal-content
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              e.stopPropagation();
              const modalContent = e.currentTarget;
              const isScrollingModal =
                modalContent.scrollHeight > modalContent.clientHeight;
              if (!isScrollingModal) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <ProfileSettings onClose={() => setShowProfileSettings(false)} />
          </div>
        </div>
      )}

      {/* Legal Disclaimers Modal */}
      {showLegalDisclaimers && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLegalDisclaimers(false);
            }
          }}
          onTouchStart={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setShowLegalDisclaimers(false);
            }
          }}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto modal-content"
            data-modal-content
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              e.stopPropagation();
              const modalContent = e.currentTarget;
              const isScrollingModal =
                modalContent.scrollHeight > modalContent.clientHeight;
              if (!isScrollingModal) {
                e.preventDefault();
              }
            }}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Legal Disclaimers
                  </h2>
                </div>
                <button
                  onClick={() => setShowLegalDisclaimers(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <Scale className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">
                      Not Legal Advice
                    </h3>
                    <p className="text-sm text-gray-700">
                      Briefed provides data and research tools only. This
                      service does not constitute legal advice. Consult
                      qualified legal professionals for legal advice and
                      representation.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">
                      Data Accuracy
                    </h3>
                    <p className="text-sm text-gray-700">
                      While we strive for accuracy, we cannot guarantee the
                      completeness or accuracy of all court data. Verify
                      important information independently before making legal
                      decisions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">
                      Professional Use
                    </h3>
                    <p className="text-sm text-gray-700">
                      This service is intended for legal professionals and
                      authorized users. Use of this service constitutes
                      acceptance of our{' '}
                      <a
                        href="/terms"
                        className="text-blue-600 hover:text-blue-700 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="/privacy"
                        className="text-blue-600 hover:text-blue-700 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => setShowLegalDisclaimers(false)}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

NewHeader.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  filters: FiltersProps.isRequired,
  setFilters: PropTypes.func.isRequired,
  courts: PropTypes.arrayOf(PropTypes.string),
  recentSearches: PropTypes.arrayOf(PropTypes.object),
  onSelectRecentSearch: PropTypes.func,
  onClearRecentSearches: PropTypes.func,
  searchExecuted: PropTypes.bool,
  setSearchExecuted: PropTypes.func,
  onClearSourceCase: PropTypes.func
};

export default NewHeader;
